<template>
  <span :class="[rootClass]">
    <slot name="label">
      <k-external-label
        v-if="externalLabel"
        :class="$attrs['disable'] ? 'text-cool-gray-60' : ''"
        :description="externalLabelDescription"
        :for="$attrs['for']"
        :label="externalLabel"
        :required="isRequired"
        :callout-title="externalLabelCalloutTitle"
        :callout-description="externalLabelCalloutDescription"
      >
        <template v-if="$slots['after-label']" #after>
          <slot name="after-label"></slot>
        </template>
      </k-external-label>
    </slot>

    <!-- FIXME @Kaveh -->
    <!-- v-scroll-outside="() => $refs.dropdownRef && $refs.dropdownRef.hidePopup()" -->
    <q-select
      ref="dropdownRef"
      v-model="componentValue"
      class="k-dropdown q-pb-none"
      :class="{
        'small-dropdown': small,
        'k-dropdown__custom-label': isCustomLabel,
      }"
      dense
      no-error-icon
      menu-self="top left"
      :menu-offset="[0, 12]"
      borderless
      options-selected-class="bg-teal-white-10 text-cool-gray-100"
      :popup-content-class="'k-dropdown-popup-content custom-scrollbar' + (small ? ' small-dropdown' : '')"
      :input-debounce="0"
      :placeholder="modelValue ? null : placeholder"
      autocomplete="off"
      map-options
      option-value="value"
      emit-value
      dropdown-icon="expand_more"
      bottom-slots
      :virtual-scroll-slice-size="Infinity"
      v-bind="{ ...$attrs, ...filterEvent }"
      :options="options"
      @new-value="createValue"
      @update:model-value="onItemSelected"
    >
      <template v-if="isCustomLabel" #before>
        <span class="flex inline" @click="$refs.dropdownRef.showPopup()">
          <slot></slot>
        </span>
      </template>

      <template v-if="hasSearch || options?.length > showSearchThreshold || searchRef !== ''" #before-options>
        <div
          class="k-dropdown__search q-px-xs q-pt-12 q-pb-sm dynamic-zoom bg-white"
          :class="options?.length === 1 && options[0].label === 'kasra-fake-label' ? 'q-mb-md' : 'q-mb-none'"
        >
          <KInput v-model="searchRef" placeholder="Search" :has-clear-icon="false" @update:model-value="handleSearch" />
        </div>
      </template>

      <template #option="{ itemProps, opt, selected }">
        <div v-if="opt[$attrs['option-label'] || 'label'] !== 'kasra-fake-label'" class="column q-pa-xs dynamic-zoom">
          <q-item
            class="q-pa-none column dropdown-item-container"
            :style="hasOptionStyle ? opt?.style : ''"
            v-bind="itemProps"
          >
            <q-item class="q-py-none">
              <q-item-section v-if="opt.icon" avatar class="q-pr-sm k-dropdown-unset-min-width">
                <KIcon :name="opt.icon" :color="opt.iconColor || 'cool-gray-70'" />
              </q-item-section>
              <q-item-section v-if="optionsMode === 'radio'" avatar class="q-pr-sm k-dropdown-unset-min-width">
                <KRadio
                  :model-value="Number(selected)"
                  :val="1"
                  class="k-dropdown-unset-min-width no-pointer-events"
                  color="space-b-100"
                  v-bind="opt.radioBind"
                />
              </q-item-section>
              <q-item-section>
                <q-item-label v-if="opt.badge">
                  <KBadge
                    :icon-color="opt.badgeIconColor || 'cool-gray-70'"
                    :icon="opt.badgeIcon"
                    :label="opt[$attrs['option-label'] || 'label']"
                    class="q-mr-sm q-py-xs"
                    outline
                  />
                </q-item-label>
                <q-item-label v-else-if="opt.status">
                  <KStatus
                    :status="opt.statusColor || '#7C8B99'"
                    :label="opt[$attrs['option-label'] || 'label']"
                    class="q-mr-sm q-py-xs"
                    outline
                  />
                </q-item-label>
                <q-item-label
                  v-else
                  v-html="opt[$attrs['option-label'] || 'label']"
                  :class="[opt['label_class'], small ? 'fs-13px' : 'fs-15px']"
                ></q-item-label>
              </q-item-section>
              <q-item-section v-if="optionsMode === 'checkbox'" class="q-pa-none" side>
                <KCheckbox
                  v-if="opt[$attrs['option-value'] || 'value'] !== selectAllValue"
                  :model-value="componentValue"
                  :value="opt[$attrs['option-value'] || 'value']"
                  color="space-b-100"
                  class="no-margin-k-checkbox"
                  v-bind="opt.checkboxBind"
                />
                <KCheckbox
                  v-else
                  :model-value="isAllSelected"
                  :value="opt[$attrs['option-value'] || 'value']"
                  color="space-b-100"
                  class="no-margin-k-checkbox"
                  v-bind="opt.checkboxBind"
                />
              </q-item-section>
            </q-item>
            <q-item v-if="opt.description" class="q-pt-none k-dropdown-unset-min-height gap-sm">
              <slot name="description" :description="opt.description">
                <q-item-section v-if="opt.descriptionIcon" avatar class="k-dropdown-unset-min-width">
                  <KIcon :name="opt.descriptionIcon" :color="opt.descriptionIconColor || ''" size="20px"></KIcon>
                </q-item-section>
                <q-item-section>
                  <q-item-label caption :class="{ 'text-cool-gray-100': selected }">
                    {{ opt.description }}
                  </q-item-label>
                </q-item-section>
              </slot>
            </q-item>
          </q-item>
        </div>
      </template>

      <template v-if="icon" #prepend>
        <KIcon :name="icon" :color="iconColor" />
      </template>
      <template
        v-if="
          rightIcon ||
          (hasClearIcon && ((!$attrs.multiple && componentValue) || ($attrs.multiple && componentValue.length > 0)))
        "
        #append
      >
        <KIcon v-if="rightIcon" :name="rightIcon" color="cool-gray-70" />

        <div v-if="!isRequired" class="row items-center q-anchor--skip k-dropdown-close-icon transition">
          <KIcon
            name="remove"
            size="20px"
            color="inherit"
            class="q-anchor--skip cursor-pointer"
            @click.stop.prevent="clear"
          />
        </div>
      </template>

      <template v-if="addValue" #after-options>
        <q-separator />
        <div v-if="showNewValue" class="q-mx-xs q-px-md q-py-sm">
          <KInput
            v-model="newValue"
            for="add-value-input"
            :external-label="addValueButtonLabel || (text.add_new ?? 'Add new ') + externalLabel"
            placeholder="Input placeholder"
            @keyup.enter="createValue"
          >
            <template #after>
              <k-btn :disable="!newValue" icon="tick-rounded" size="md" model="link" @click="createValue" />
            </template>
          </KInput>
        </div>
        <div v-else class="q-px-md q-py-sm">
          <k-btn icon="plus-rounded" color="space-b-100" size="md" model="link" @click="showNewValue = true" />
          <span class="fs-15px fw-500 text-cool-gray-80 cursor-pointer q-ml-sm" @click="showNewValue = true">
            {{ addValueLabel || (text.add_more ?? 'Add more ') + externalLabel }}
          </span>
        </div>
      </template>

      <template
        v-if="
          (modelValue === null || (typeof modelValue === 'object' && !Object.keys(modelValue).length)) &&
          !$attrs['use-input']
        "
        #selected
      >
        <div class="dropdown-placeholder">{{ placeholder }}</div>
      </template>

      <template #selected-item="{ index, opt }">
        <template
          v-if="modelValue && typeof modelValue === 'object' && Object.keys(modelValue).length"
          :style="hasOptionStyle ? opt?.style : ''"
        >
          <span v-if="allowAllSelection && Object.keys(modelValue).length === $attrs.options?.length - 1">
            <span v-if="index === 0">{{ text.select_all ?? 'Select All' }}</span>
          </span>
          <span v-else :style="hasOptionStyle ? opt?.style : ''">
            <span v-if="index === 0" v-html="opt[$attrs['option-label'] || 'label']"></span>
            <span v-if="index === 1 && Array.isArray(modelValue) && modelValue.length > 1">
              , +{{ modelValue.length - 1 }} {{ text.more ?? 'more' }}
            </span>
          </span>
        </template>
        <template v-else>
          <span :style="hasOptionStyle ? opt?.style : ''" v-html="opt[$attrs['option-label'] || 'label']?.replace(/nbsp/g, '')?.replace(/&;/g, '')">
          </span>
        </template>
      </template>

      <template v-for="slot of Object.keys($slots)" #[slot]>
        <slot :name="slot"></slot>
      </template>
    </q-select>
  </span>
</template>

<script setup>
import { computed, ref, useAttrs, watch } from 'vue'

const attrs = useAttrs()

defineOptions({ name: 'KDropdown', inheritAttrs: false })
const props = defineProps({
  modelValue: { type: [Object, String, Number, null], required: true },
  rootClass: { type: [Object, String, Array], default: '' },
  icon: { type: String, default: null },
  iconColor: { type: String, default: 'cool-gray-70' },
  rightIcon: { type: String, default: null },
  hasClearIcon: { type: Boolean, default: false },
  addValue: { type: Boolean, default: false },
  addValueLabel: { type: String, default: '' },
  addValueButtonLabel: { type: String, default: '' },
  placeholder: { type: String, default: 'Dropdown Placeholder' },
  isRequired: { type: Boolean, default: false },
  optionsMode: {
    type: String,
    default: 'label',
    validator(value) {
      return ['label', 'checkbox', 'radio'].includes(value)
    },
  },
  externalLabel: { type: String, default: '' },
  externalLabelDescription: { type: String, default: '' },
  externalLabelCalloutTitle: { type: String, default: '' },
  externalLabelCalloutDescription: { type: String, default: '' },
  small: { type: Boolean, default: false },
  isCustomLabel: { type: Boolean, default: false },
  allowAllSelection: { type: Boolean, default: false },
  text: { type: Object, default: () => ({}) },
  hasSearch: { type: Boolean, default: false },
  showSearchThreshold: { type: Number, default: 10 },
  sortByLabel: { type: Boolean, default: false },
  resetValidationOnInput: { type: Boolean, default: true },
  hasOptionStyle: { type: Boolean, default: false },
  defaultValue: { type: [Number, String], default: null },
})
const emit = defineEmits(['update:modelValue', 'new-value'])

const defaultValue =
  props.defaultValue !== undefined
    ? props.defaultValue
    : typeof props.modelValue === 'number'
      ? 0
      : typeof props.modelValue === 'string'
        ? ''
        : undefined
let componentValue = ref(
  props.modelValue !== null && props.modelValue !== void 0 ? props.modelValue : attrs.multiple ? [] : defaultValue,
)
let showNewValue = ref(false)
let newValue = ref(null)
const labelKey = attrs['option-label'] || 'label'
const options = ref(
  !props.sortByLabel ? attrs.options : attrs.options.sort((a, b) => a[labelKey]?.localeCompare(b[labelKey])) || [],
)
const isAllSelected = ref(false)
const searchRef = ref('')
const selectAllValue = -1
const dropdownRef = ref(null)
const selectAllOption = {
  [attrs['option-label'] || 'label']: props.text.select_all ?? 'Select All',
  [attrs['option-value'] || 'value']: selectAllValue,
}

if (attrs.multiple !== undefined && props.optionsMode === 'checkbox' && props.allowAllSelection) {
  const existingOption = options.value.find((option) => option[attrs['option-value'] || 'value'] === selectAllValue)
  if (!existingOption && options.value.length > 1) options.value.unshift(selectAllOption)
  if (componentValue.value.length === options.value.length - 1) isAllSelected.value = true
}

watch(
  () => props.modelValue,
  (newValue) => {
    componentValue.value = newValue
  },
)

watch(
  () => attrs.options,
  (newValue) => {
    options.value = newValue
  },
)

watch(componentValue, (newValue) => {
  isAllSelected.value = newValue?.length === options.value.length - 1
  if (props.resetValidationOnInput) dropdownRef.value.resetValidation()
  emit('update:modelValue', newValue)
})

function handleSearch() {
  const labelKey = attrs['option-label'] || 'label'
  const needle = searchRef.value.toLowerCase()
  const sanitisedOptions = attrs.options.filter((option) => option[attrs['option-value'] || 'value'] !== selectAllValue)
  const haystack = !props.sortByLabel
    ? sanitisedOptions
    : sanitisedOptions.sort((a, b) => a[labelKey]?.localeCompare(b[labelKey]))

  const result = haystack.filter((option) => option[labelKey].toLowerCase().includes(needle))
  options.value = result.length ? result : [{ [labelKey]: 'kasra-fake-label', value: 0 }]

  if (!searchRef.value && attrs.multiple !== undefined && props.optionsMode === 'checkbox' && props.allowAllSelection)
    options.value.unshift(selectAllOption)
}

function onItemSelected(value) {
  if (Array.isArray(value) && value.includes(selectAllValue) && props.allowAllSelection) {
    isAllSelected.value = value?.length < options.value.length - 1
    if (isAllSelected.value) {
      componentValue.value = options.value
        .filter((option) => option[attrs['option-value'] || 'value'] !== selectAllValue)
        .map((option) => option[attrs['option-value'] || 'value'])
    } else componentValue.value = []
  } else componentValue.value = value
}

function createValue() {
  if (newValue.value) {
    emit('new-value', newValue.value)
    newValue.value = ''
  }
}

function filterFn(val, update) {
  if (val === '') {
    update(() => {
      options.value = attrs.options // show all options if no filter
    })
    return
  }
  update(() => {
    const needle = val.toLowerCase()
    options.value = attrs.options.filter((v) => v[attrs['option-label'] || 'label'].toLowerCase().includes(needle))
  })
}

function clear() {
  if (attrs.multiple !== undefined) componentValue.value = []
  else componentValue.value = defaultValue
}

const filterEvent = computed(() => {
  if (attrs['use-input']) return { onFilter: (attrs && attrs.onFilter && attrs.onFilter) || filterFn }
  return {}
})
</script>

<style lang="sass">
.k-dropdown-popup-content .q-focus-helper
  display: none
.k-dropdown-popup-content
  box-shadow: 0 6px 15px 0 rgba(24, 40, 77, 0.12)
  color: var(--cool-gray-80)
  z-index: 6100 // @Kasra z-index
  max-height: 450px !important
.k-dropdown-popup-content .dropdown-item-container
  border-radius: 12px
.no-margin-k-checkbox .k-checkbox__input
  margin: 0 !important

.k-dropdown-popup-content,
%k-dropdown-default .q-field__inner,
%k-dropdown-default .q-field__input
  border-radius: 12px !important

%k-dropdown-default .q-field__native
  padding: 0
// %k-dropdown-default.small-dropdown .q-field__native
//   padding-left: 10px !important
//   padding-right: 10px !important

%k-dropdown-default .q-field__native .q-field__input
  padding-top: 6px
  padding-bottom: 6px
  height: 100%

%k-dropdown-default:not(.small-dropdown):not(.small-input) .q-field__control
  padding-left: 12px !important
  padding-right: 12px !important
  min-height: 46px !important
  display: flex
  align-items: center
%k-dropdown-default.small-dropdown .q-field__control
  padding-left: 8px !important
  padding-right: 8px !important
  min-height: 38px !important
  display: flex
  align-items: center
%k-dropdown-default.small-input .q-field__control
  padding-left: 16px !important
  padding-right: 16px !important
%k-dropdown-default textarea
  padding-top: 12px !important
  padding-bottom: 12px !important
%k-dropdown-default .q-field__append:not(.q-anchor--skip)
  padding-left: 0
  margin-left: 8px
%k-dropdown-default .q-field__append.q-anchor--skip
  padding-left: 0
%k-dropdown-default .q-field__prepend
  padding-right: 0
  margin-right: 8px
%k-dropdown-default .q-anchor--skip button
  width: 20px
  height: 20px
%k-dropdown-default.q-field
  font-size: var(--fs-15px, 15px)
%k-dropdown-default.small-dropdown.q-field
  font-size: var(--fs-13px, 13px)



@mixin kDropdownColor($color)
  .q-field__inner input,
  .q-field__input input,
  .q-field__inner span,
  textarea
    color: $color
  //font-weight: bold
  .q-spinner
    color: $color
  &.q-field--readonly .q-field__inner
    border: 1px dashed var(--cool-gray-60)
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus,
  select:-webkit-autofill,
  select:-webkit-autofill:hover,
  select:-webkit-autofill:focus
    -webkit-text-fill-color: $color
    caret-color: $color
  .q-anchor--skip button
    color: $color
    opacity: 1


@mixin kDropdownErrorColor($color, $innerColor, $shadowColor: var(--error-20))
  &.q-field--error .q-field__inner
    border: 1px solid $color !important
    &:hover
      box-shadow: 0 0 0 3px $shadowColor !important
  &.q-field--error.q-field--focused .q-field__inner
    box-shadow: 0 0 0 3px $shadowColor !important
  &.q-field--error .q-field__messages
    color: $color !important
    font-size: var(--fs-13px, 13px)
  &.q-field--error .q-select__dropdown-icon
    color: $innerColor !important
  &.q-field--error .q-icon
    color: $innerColor !important
  &.q-field--error i
    fill: $innerColor !important
    color: $innerColor !important
  &.q-field--error i *
    fill: $innerColor !important
    color: $innerColor !important
  &.q-field--error .q-field__inner input,
  &.q-field--error .q-field__input input,
  &.q-field--error .q-field__inner span,
  &.q-field--error textarea
    color: $innerColor
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus,
  select:-webkit-autofill,
  select:-webkit-autofill:hover,
  select:-webkit-autofill:focus
    -webkit-text-fill-color: $innerColor
    caret-color: $innerColor

@mixin kDropdownPlaceholderColor($color)
  .q-field__inner input::placeholder,
  .q-field__input input::placeholder,
  textarea::placeholder
    color: $color
    opacity: 1
  .q-field__inner input:-ms-input-placeholder,
  .q-field__input input:-ms-input-placeholder,
  textarea:-ms-input-placeholder
    color: $color
    opacity: 1
  .q-field__inner input::-ms-input-placeholder,
  .q-field__input input::-ms-input-placeholder,
  textarea::-ms-input-placeholder
    color: $color
    opacity: 1
  .placeholder,
  .dropdown-placeholder
    color: $color
    opacity: 1
  .dropdown-placeholder
    margin-top: 1px
  .q-select__dropdown-icon
    fill: $color
    color: $color

@mixin kDropdownBorderColor($borderColor, $focusBorderColor)
  .q-field__inner
    transition: 0.3s ease
    border: 1px solid $borderColor
  .q-field__inner:hover
    border: 1px solid $focusBorderColor
    box-shadow: 0 0 0 3px var(--teal-white-20)
  &.q-field--focused .q-field__inner
    border: 1px solid $focusBorderColor
    box-shadow: 0 0 0 3px var(--teal-white-20)

.k-dropdown
  border-radius: 12px
  @extend %k-dropdown-default
  @include kDropdownColor(var(--cool-gray-100))
  @include kDropdownPlaceholderColor(var(--cool-gray-60))
  @include kDropdownBorderColor(var(--cool-gray-40), var(--space-b-50))
  @include kDropdownErrorColor(var(--error-100), var(--error-100))
  transition: padding-bottom 0.3s
  &:has(.q-field__bottom [role="alert"])
    padding-bottom: 20px

  .q-field__bottom
    display: none
  .q-field__bottom:has([role="alert"])
    display: flex
  &__search
    position: sticky
    top: 0
    z-index: 2

.k-dropdown-unset-min-width
  min-width: unset !important

.k-dropdown-unset-min-height
  min-height: unset !important


.k-dropdown-close-icon
  color: var(--cool-gray-70)
  &:hover
    color: var(--cool-gray-100)

.k-dropdown.q-field--disabled
  .q-field__inner
    background-color: var(--off-white-20)
    color: var(--cool-gray-60)
    border-color: var(--off-white-20)
    box-shadow: none

.k-dropdown__custom-label
  position: relative
  .q-field__inner
    position: absolute
    visibility: hidden
    pointer-events: none
    height: 100%
  .q-field__before
    padding-right: 0
    height: auto
</style>
